import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { deepPure } from '~/util';
import {
  useResourcingChartToolbarContext,
  useSelectedResourceRequestContext
} from '~/modules/projects/resourcing-plan/hooks';
import { useHasPermission } from '~/modules/common/permissions';
import { useMeContext } from '~/modules/me';
import { useSessionStorage, getSessionStorage } from '~/modules/common/hooks';
import { useProjectContext } from '~/modules/resourcing/common/contexts';
import { useUpdateResourceAllocation } from '~/modules/resourcing/hooks';
import useResourceAllocationChartUserRowActions from '../../hooks/useResourceAllocationChartUserRowActions';
import { GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES_VARIABLES } from '../../../../hooks/useFetchResourcePlanUsersAndRoles';
import {
  LeftItemActions,
  LeftItemContentModel0,
  LeftItemContentModel1
} from './components';

const useStyles = makeStyles(theme => ({
  rolesContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    flexBasis: theme.spacing(17.5),
    minWidth: theme.spacing(12.5),
    borderLeft: `1px solid ${theme.palette.divider}`
  },
  userSelectionWrapper: {
    display: 'flex',
    paddingTop: theme.spacing(0.875),
    padding: theme.spacing(0, 1),
    width: theme.spacing(28.75)
  },
  userSelectionWrapperWithNoActions: {
    width: theme.spacing(31.75),
    paddingLeft: theme.spacing(4)
  },
  rowActions: {
    marginTop: theme.spacing(1)
  },
  readOnlyUser: {
    width: theme.spacing(31.75),
    padding: theme.spacing(0, 1, 0, 4)
  },
  readOnlyUserWithActions: {
    width: theme.spacing(28.75),
    padding: theme.spacing(0, 1)
  }
}));

const useUserStyles = makeStyles(theme => ({
  label: {
    maxWidth: theme.spacing(19)
  }
}));

const useActionsStyles = makeStyles(() => ({
  secondaryActionsButton: {
    marginLeft: 0,
    padding: 0
  },
  actionsPlaceholder: {}
}));

const useModel1ActionsStyles = makeStyles(() => ({
  actionsPlaceholder: {
    width: 0
  }
}));

const useActualsStyles = makeStyles(theme => ({
  roleSelection: {
    paddingBottom: theme.spacing(5)
  },
  roleRowCellReadonly: {
    height: theme.spacing(9)
  }
}));

const useRowStyles = makeStyles(theme => ({
  roleSelection: {
    paddingBottom: theme.spacing(1)
  },
  roleRowCellReadonly: {
    paddingBottom: theme.spacing(1)
  }
}));

const hasEditTeamPermission = permittedActionUris =>
  permittedActionUris.includes('urn:replicon:project-action:edit-team');

const hasEditProjectPermission = permittedActionUris =>
  permittedActionUris.includes('urn:replicon:project-action:edit-project');

export const ResourceAllocationChartLeftItem2 = ({
  allocatedUser,
  autofocusedUserId,
  handleRemoveResourceAllocation,
  isEditEnabled,
  isUserRowExpanded,
  isUserTaskAllocationsOverDistributed,
  onReleaseResourceAllocationSuccess,
  onUserChange,
  onUserRowExpansionPanelClick,
  setAutofocusedUserId,
  setSnackBarState,
  resetResourceAllocationChart,
  roleScheduleAsOfDate
}) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();
  const actualsClasses = useActualsStyles();
  const rowStyles = useRowStyles();
  const userClasses = useUserStyles();
  const {
    dateRange: chartDateRange,
    isResourceActualModeEnabled
  } = useResourcingChartToolbarContext();
  const {
    project,
    projectObjectPermissions: { canEditResourceAllocation }
  } = useProjectContext();

  const { openResourceRequestDrawer } = useSelectedResourceRequestContext();

  const resourcePlanAllocatedUsersAndRolesCacheVariables = getSessionStorage(
    GET_RESOURCE_PLAN_ALLOCATED_USERS_AND_ROLES_VARIABLES
  );

  const { storedValue: resourceRequestsQueryVariables } = useSessionStorage(
    'RESOURCE-REQUESTS-QUERY-VARIABLES',
    null
  );
  const actionsClasses = useActionsStyles();
  const model1Classes = useModel1ActionsStyles();
  const {
    featureFlags: {
      isPsaRmpTaskAllocation1Enabled,
      isPsaRmpSetResourceAllocationEnabled,
      isPsaRmpEnhancedProjectManagerPersonaEnabled
    }
  } = useMeContext();

  const {
    onUpdateResourceAllocation,
    loading: isUpdateLoading
  } = useUpdateResourceAllocation();

  const actionsClassesFFOn = useMemo(
    () => ({
      ...actionsClasses,
      ...model1Classes
    }),
    [actionsClasses, model1Classes]
  );

  const isCompleteResourceAllocationEditEnabled = useHasPermission({
    actionUri: 'urn:replicon-webui:completed-resource-allocation:edit'
  });

  const {
    resourceAllocationReferencesForUser,
    resourceRequestIdsForUser,
    hasResourceAllocations
  } = allocatedUser.roles.reduce(
    (retVal, { role, resourceAllocationReference }) => {
      if (resourceAllocationReference.length) {
        return {
          resourceAllocationReferencesForUser: [
            ...retVal.resourceAllocationReferencesForUser,
            ...resourceAllocationReference.map(({ id }) => ({
              key: id,
              allocationId: id,
              role
            }))
          ],
          resourceRequestIdsForUser: [
            ...retVal.resourceRequestIdsForUser,
            ...resourceAllocationReference.reduce(
              (accRef, currRef) => [...accRef, ...currRef.resourceRequestIds],
              []
            )
          ],
          hasResourceAllocations: true
        };
      }

      return {
        ...retVal,
        resourceAllocationReferencesForUser: [
          ...retVal.resourceAllocationReferencesForUser,
          { key: role?.id || `${allocatedUser.user.uri}-no-role`, role }
        ]
      };
    },
    {
      resourceAllocationReferencesForUser: [],
      resourceRequestIdsForUser: [],
      hasResourceAllocations: false
    }
  );

  const hasEditPermissionForReleaseResource = isPsaRmpEnhancedProjectManagerPersonaEnabled
    ? canEditResourceAllocation
    : hasEditProjectPermission(project.permittedActionUris);

  const canReleaseResourceAllocation =
    (hasEditPermissionForReleaseResource &&
      hasEditTeamPermission(project.permittedActionUris)) ||
    isCompleteResourceAllocationEditEnabled;

  const {
    actions,
    showViewMultipleResourceRequestsDialog,
    onViewMultipleResourceRequestsDialogClose,
    releaseAllocationLoading,
    showReleaseResourcesDialog,
    closeReleaseResourcesDialog,
    releaseResourceAllocation,
    showChangeResourceDialog,
    closeChangeResourceDialog,
    showSetResourceAllocationDialog,
    closeSetResourceAllocationDialog
  } = useResourceAllocationChartUserRowActions({
    resourceRequestIds: resourceRequestIdsForUser,
    projectId: project.id,
    userId: allocatedUser.user.id,
    userName: allocatedUser.user.displayText,
    handleRemoveResourceAllocation,
    showReleaseResourceAction:
      hasResourceAllocations && canReleaseResourceAllocation,
    resourcePlanAllocatedUsersAndRolesCacheVariables,
    resourceRequestCacheUpdateProps: {
      resourceRequestsQueryVariables
    },
    setSnackBarState,
    showChangeResourceAction: Boolean(
      isPsaRmpTaskAllocation1Enabled &&
        isCompleteResourceAllocationEditEnabled &&
        hasResourceAllocations
    ),
    showSetResourceAllocationAction: Boolean(
      isPsaRmpTaskAllocation1Enabled &&
        isPsaRmpSetResourceAllocationEnabled &&
        isCompleteResourceAllocationEditEnabled &&
        hasResourceAllocations
    )
  });

  const qeIdTag = `${allocatedUser.user.displayText}_AllocationRow`;

  const actionsComponentProps = useMemo(
    () => ({
      onReleaseResourceAllocationSuccess,
      actionsClasses: isPsaRmpTaskAllocation1Enabled
        ? actionsClassesFFOn
        : actionsClasses,
      showReleaseResourcesDialog,
      project,
      releaseResourceAllocation,
      closeReleaseResourcesDialog,
      releaseAllocationLoading,
      showViewMultipleResourceRequestsDialog,
      onViewMultipleResourceRequestsDialogClose,
      openResourceRequestDrawer,
      showChangeResourceDialog,
      closeChangeResourceDialog,
      showSetResourceAllocationDialog,
      closeSetResourceAllocationDialog,
      onUpdateResourceAllocation,
      isUpdateLoading,
      actions,
      qeIdTag,
      allocatedUser,
      resetResourceAllocationChart
    }),
    [
      onReleaseResourceAllocationSuccess,
      actions,
      actionsClasses,
      actionsClassesFFOn,
      allocatedUser,
      closeReleaseResourcesDialog,
      isPsaRmpTaskAllocation1Enabled,
      onViewMultipleResourceRequestsDialogClose,
      openResourceRequestDrawer,
      project,
      qeIdTag,
      releaseAllocationLoading,
      releaseResourceAllocation,
      showReleaseResourcesDialog,
      showViewMultipleResourceRequestsDialog,
      showChangeResourceDialog,
      closeChangeResourceDialog,
      showSetResourceAllocationDialog,
      closeSetResourceAllocationDialog,
      onUpdateResourceAllocation,
      isUpdateLoading,
      resetResourceAllocationChart
    ]
  );

  return (
    <>
      {isPsaRmpTaskAllocation1Enabled ? (
        <LeftItemContentModel1
          allocatedUser={allocatedUser}
          isUserRowExpanded={isUserRowExpanded}
          ActionsComponent={LeftItemActions}
          actionsComponentProps={actionsComponentProps}
          onUserRowExpansionPanelClick={onUserRowExpansionPanelClick}
          isUserTaskAllocationsOverDistributed={
            isUserTaskAllocationsOverDistributed
          }
          roleScheduleAsOfDate={roleScheduleAsOfDate}
        />
      ) : (
        <LeftItemContentModel0
          hasResourceAllocations={hasResourceAllocations}
          isEditAllowed={isCompleteResourceAllocationEditEnabled}
          classes={classes}
          userClasses={userClasses}
          chartDateRange={chartDateRange}
          onUserChange={onUserChange}
          allocatedUser={allocatedUser}
          formatMessage={formatMessage}
          resourceAllocationReferencesForUser={
            resourceAllocationReferencesForUser
          }
          isResourceActualModeEnabled={isResourceActualModeEnabled}
          actualsClasses={actualsClasses}
          rowStyles={rowStyles}
          ActionsComponent={LeftItemActions}
          actionsComponentProps={actionsComponentProps}
          qeIdTag={qeIdTag}
          actions={actions}
          autofocusedUserId={autofocusedUserId}
          setAutofocusedUserId={setAutofocusedUserId}
        />
      )}
    </>
  );
};

ResourceAllocationChartLeftItem2.propTypes = {
  allocatedUser: PropTypes.object,
  onUserChange: PropTypes.func.isRequired,
  handleRemoveResourceAllocation: PropTypes.func,
  isEditEnabled: PropTypes.bool,
  setSnackBarState: PropTypes.func,
  autofocusedUserId: PropTypes.string,
  setAutofocusedUserId: PropTypes.func,
  isUserRowExpanded: PropTypes.bool,
  onUserRowExpansionPanelClick: PropTypes.func,
  onReleaseResourceAllocationSuccess: PropTypes.func,
  isUserTaskAllocationsOverDistributed: PropTypes.bool,
  resetResourceAllocationChart: PropTypes.func.isRequired,
  roleScheduleAsOfDate: PropTypes.object
};

export default deepPure(ResourceAllocationChartLeftItem2);
